import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";

/**
 * Work With Us Page - STEAM Studios
 * Showcasing our three core service offerings
 */

const SERVICES = [
  {
    title: "Product & Company Launch Campaigns",
    tagline: "From Zero to Market Dominance",
    description: "We architect comprehensive launch narratives that command attention and drive adoption. From brand identity to go-to-market strategy, we build the story infrastructure that transforms announcements into movements.",
    capabilities: [
      "Brand identity & visual systems",
      "Launch event production & live experiences",
      "Multi-channel content strategy & execution",
      "Media relations & thought leadership positioning",
      "Community building & audience activation"
    ],
    accent: "#1F4FFF",
    icon: "🚀"
  },
  {
    title: "Digital Shows & Podcasts",
    tagline: "Broadcast-Quality. Built for Impact.",
    description: "High-production video series and audio programs engineered for engagement and distribution. We handle everything from concept to post-production, delivering content that builds authority and captures mindshare.",
    capabilities: [
      "Concept development & show design",
      "On-location & studio production",
      "Post-production & editorial",
      "Distribution strategy & platform optimization",
      "Audience growth & monetization"
    ],
    accent: "#FFB300",
    icon: "🎬"
  },
  {
    title: "Longitudinal Storytelling & Docu-Follows",
    tagline: "Long-Form Narratives That Build Legacies",
    description: "Deep-dive documentary series that track innovation over time. We embed with companies, capture the journey, and craft serialized content that transforms milestones into mythology—building brand equity that compounds.",
    capabilities: [
      "Multi-year narrative architecture",
      "Embedded production & story capture",
      "Character-driven documentary filmmaking",
      "Serialized content for sustained engagement",
      "Archive & legacy preservation"
    ],
    accent: "#1F4FFF",
    icon: "📽️"
  }
];

export default function WorkWithUs() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.3], [0, -50]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.25], [1, 0.4]);

  return (
    <div className="min-h-screen w-full bg-[#0A0B0D] text-zinc-100 [--acc1:#1F4FFF] [--acc2:#FFB300] selection:bg-zinc-100 selection:text-black">
      <Grain />

      {/* HERO SECTION */}
      <section className="relative isolate flex min-h-[90vh] items-center justify-center overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0A0B0D] to-[#0A0B0D]" />

        {/* Ambient glows */}
        <div className="pointer-events-none absolute inset-0 opacity-40">
          <div
            className="absolute left-1/4 top-20 h-96 w-96 rounded-full blur-3xl"
            style={{ background: "radial-gradient(closest-side, var(--acc1), transparent 70%)" }}
          />
          <div
            className="absolute right-1/4 top-40 h-[32rem] w-[32rem] rounded-full blur-[100px]"
            style={{ background: "radial-gradient(closest-side, var(--acc2), transparent 70%)" }}
          />
        </div>

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 w-full max-w-5xl px-6 text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-6 inline-block rounded-full border border-white/20 bg-white/[0.05] px-5 py-2 text-[13px] uppercase tracking-[0.2em] text-zinc-300 backdrop-blur-sm"
          >
            Projects & Services
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="mx-auto mb-8 max-w-4xl font-black leading-[0.9] tracking-tight font-spartan"
            style={{
              fontSize: "clamp(48px, 10vw, 140px)",
              background: "linear-gradient(135deg, #ffffff 0%, #a0a0a0 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              filter: "brightness(1.2)"
            }}
          >
            Work With Us
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="mx-auto max-w-2xl text-[clamp(16px,1.6vw,22px)] leading-relaxed text-zinc-300/90"
          >
            We build narrative infrastructure for companies and ideas that matter.
            From launch campaigns to long-form storytelling, we fight for your story.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.8 }}
            className="mt-12 flex justify-center gap-4"
          >
            <a
              href="mailto:Contact@steamstudio.io"
              className="group relative overflow-hidden rounded-full border border-white/25 bg-white/[0.08] px-8 py-4 text-[15px] font-bold tracking-wide text-zinc-100 transition-all duration-300 hover:border-white/50 hover:bg-white/[0.15] hover:scale-105 backdrop-blur-md"
            >
              <div className="pointer-events-none absolute -left-6 top-1/2 h-14 w-14 -translate-y-1/2 rounded-full blur-xl opacity-50 transition-opacity duration-300 group-hover:opacity-70" style={{ background: "radial-gradient(closest-side, var(--acc2), transparent)" }} />
              <span className="relative z-10">Start a Project</span>
            </a>
            <Link
              to="/"
              className="rounded-full border border-white/15 bg-transparent px-8 py-4 text-[15px] font-semibold tracking-wide text-zinc-300 transition-all duration-300 hover:border-white/30 hover:bg-white/[0.05] hover:text-zinc-100"
            >
              Back to Home
            </Link>
          </motion.div>
        </motion.div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />
      </section>

      {/* SERVICES SECTION */}
      <section className="relative mx-auto max-w-7xl px-6 py-24">
        <div className="mb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-4 text-[13px] uppercase tracking-[0.22em] text-zinc-400/80"
          >
            Three Core Offerings
          </motion.div>
          <TitleSweep>What We Build</TitleSweep>
        </div>

        <div className="space-y-8">
          {SERVICES.map((service, idx) => (
            <ServiceCard key={service.title} {...service} index={idx} />
          ))}
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="relative mx-auto max-w-5xl px-6 py-32">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-3xl border border-white/15 bg-gradient-to-br from-white/[0.08] to-white/[0.02] p-12 md:p-16 text-center"
        >
          <div className="pointer-events-none absolute -left-32 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full blur-[120px] opacity-40" style={{ background: "radial-gradient(closest-side, var(--acc1), transparent)" }} />
          <div className="pointer-events-none absolute -right-32 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full blur-[120px] opacity-40" style={{ background: "radial-gradient(closest-side, var(--acc2), transparent)" }} />

          <div className="relative z-10">
            <h2 className="mb-6 text-[clamp(32px,5vw,56px)] font-black leading-tight tracking-tight font-spartan">
              Ready to Build Something Exceptional?
            </h2>
            <p className="mx-auto mb-10 max-w-2xl text-[clamp(15px,1.4vw,18px)] text-zinc-300/90">
              Whether you're launching a company, producing a show, or documenting a multi-year journey—we're ready to fight for your story.
            </p>
            <a
              href="mailto:Contact@steamstudio.io"
              className="group relative inline-block overflow-hidden rounded-full border border-white/30 bg-white/[0.12] px-10 py-5 text-[16px] font-bold tracking-wide text-zinc-100 transition-all duration-300 hover:border-white/50 hover:bg-white/[0.2] hover:scale-105 backdrop-blur-md"
            >
              <div className="pointer-events-none absolute -left-8 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full blur-xl opacity-50 transition-opacity duration-300 group-hover:opacity-70" style={{ background: "radial-gradient(closest-side, var(--acc1), transparent)" }} />
              <div className="pointer-events-none absolute -right-8 top-1/2 h-16 w-16 -translate-y-1/2 rounded-full blur-xl opacity-40 transition-opacity duration-300 group-hover:opacity-60" style={{ background: "radial-gradient(closest-side, var(--acc2), transparent)" }} />
              <span className="relative z-10">Get in Touch</span>
            </a>
          </div>
        </motion.div>
      </section>

      {/* FOOTER */}
      <footer className="mx-auto max-w-7xl px-6 py-16 text-zinc-400/80">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
          <p className="text-xs">© {new Date().getFullYear()} STEAM Studios</p>
          <nav className="flex gap-6 text-xs">
            <Link to="/" className="hover:text-zinc-100 transition">Home</Link>
            <a href="mailto:Contact@steamstudio.io" className="hover:text-zinc-100 transition">Contact</a>
          </nav>
        </div>
      </footer>

      <StyleBlocks />
    </div>
  );
}

// -------------------- COMPONENTS --------------------

function ServiceCard({ title, tagline, description, capabilities, accent, icon, index }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, delay: index * 0.15 }}
      viewport={{ once: true, amount: 0.2 }}
      className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-12 hover:border-white/20 transition-all duration-500"
    >
      {/* Accent glow */}
      <div
        className="pointer-events-none absolute -left-32 top-1/2 h-80 w-80 -translate-y-1/2 rounded-full blur-[100px] opacity-25 transition-opacity duration-500 group-hover:opacity-40"
        style={{ background: `radial-gradient(closest-side, ${accent}, transparent 70%)` }}
      />

      <div className="relative z-10">
        <div className="mb-6 flex items-start justify-between">
          <div className="flex-1">
            <div className="mb-3 text-5xl">{icon}</div>
            <h3 className="mb-2 text-[clamp(28px,3.5vw,48px)] font-extrabold leading-tight tracking-tight font-spartan">
              {title}
            </h3>
            <p className="text-[clamp(14px,1.3vw,18px)] font-semibold text-zinc-300/90" style={{ color: accent }}>
              {tagline}
            </p>
          </div>
        </div>

        <p className="mb-8 max-w-3xl text-[clamp(15px,1.2vw,17px)] leading-relaxed text-zinc-300/85">
          {description}
        </p>

        <div className="space-y-3">
          <h4 className="text-[14px] font-bold uppercase tracking-[0.15em] text-zinc-400/90">
            Key Capabilities
          </h4>
          <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {capabilities.map((cap, i) => (
              <li key={i} className="flex items-start gap-3 text-[clamp(13px,1.1vw,15px)] text-zinc-300/80">
                <span className="mt-1.5 block h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: accent }} />
                <span>{cap}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-white/10" />
    </motion.article>
  );
}

function TitleSweep({ children }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 80%", "center 40%", "end 20%"]
  });

  const bgX = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], ["150%", "50%", "0%", "-150%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.5, 0.85, 1], [0.5, 0.8, 1, 0.8, 0.6]);

  return (
    <motion.h2
      ref={ref}
      style={{
        backgroundPositionX: bgX,
        opacity,
        WebkitTextStroke: "0.5px rgba(255,255,255,0.15)"
      }}
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: false }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="text-balance text-[clamp(32px,6vw,64px)] font-black leading-[0.95] tracking-tight bg-[linear-gradient(90deg,theme(colors.zinc.600),white,theme(colors.zinc.600))] bg-[length:300%_100%] bg-clip-text text-transparent font-spartan"
    >
      {children}
    </motion.h2>
  );
}

// -------------------- STYLES & UTILS --------------------
function Grain() {
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 z-[1] opacity-[0.07] mix-blend-soft-light" style={{ backgroundImage: `url('data:image/svg+xml;utf8,${encodeURIComponent(GRAIN_SVG)}')` }} />
  );
}

function StyleBlocks() {
  return (
    <style>{`
      h1, h2, h3 {
        letter-spacing: var(--tight);
        font-family: 'League Spartan', Inter, ui-sans-serif, system-ui;
      }

      html {
        scroll-behavior: smooth;
      }
    `}</style>
  );
}

const GRAIN_SVG = `
<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'>
  <filter id='n'>
    <feTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/>
  </filter>
  <rect width='100%' height='100%' filter='url(%23n)' opacity='0.75'/>
</svg>`;
